import React, { FC, memo, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { LocationState } from "../../types"
import { Button, Card } from "antd"
import { BookerPaymentMethodInput, GetUpdatedBookerQuery, useGetUpdatedBookerQuery } from "../../generated/graphql"
import { URL_ACCOUNT, URL_REGISTER_SEPA_EDIT_ID } from "../../models/url"
import SepaDebitEditForm from "./SepaDebitEditForm"
import { EditOutlined } from "@ant-design/icons"
import css from "./SepaData.module.css"
import { safeStorage } from "../../helpers/safeStorage"

type SepaDataInput = {
    sepaAccountholder?: string
    iban?: string
    bank?: string
    bic?: string
}

type SepaDataEditProps = {
    id?: string
}

const SepaDataEdit: FC<SepaDataEditProps> = (/* { formikProps, companyMethods } */ id /* , bookerId */) => {
    const [booker, setBooker] = React.useState<GetUpdatedBookerQuery["booker"]>()
    const location = useLocation<LocationState & { bookerId: string }>()
    const paymentMethodId = id.id
    const fromUrl = location.state?.fromUrl /* ?? { pathname: URL_BASE } */
    const bookerId = location.state?.bookerId
    const loggedInUser = safeStorage.getItem("loggedInUser")
    const isLoggedIn = Boolean(loggedInUser)

    const { data: bookerData, loading: loadingBookerData } = useGetUpdatedBookerQuery({
        skip: !isLoggedIn || !bookerId,
        variables: { id: bookerId ?? "" },
        pollInterval: 120000,
    })

    useEffect(() => {
        if (bookerData?.booker) {
            setBooker(bookerData.booker)
        }
    }, [bookerData])

    const allPaymentMethods = booker?.paymentMethods
    const sepaPaymentMethods = allPaymentMethods?.filter((pm) => pm.sepaAccountholder)

    if (loadingBookerData) return <div>Loading...</div>
    if (!booker) return <div>Booker not found</div>

    if (paymentMethodId) {
        const paymentMethodToEdit = sepaPaymentMethods?.find((pm) => pm.id === paymentMethodId)
        if (!paymentMethodToEdit) return <div>Payment method not found</div>

        const pm: SepaDataInput = {
            sepaAccountholder: paymentMethodToEdit.sepaAccountholder ?? undefined,
            bank: paymentMethodToEdit.bank ?? undefined,
            iban: paymentMethodToEdit.iban ?? undefined,
            bic: paymentMethodToEdit.bic ?? undefined,
        }

        return <SepaDebitEditForm pm={pm} paymentMethodId={paymentMethodId} fromUrl={fromUrl} bookerId={booker.id} />
    }

    const SepaDataRender = ({ data }: { data: BookerPaymentMethodInput }) => {
        const shortenedIban = data.iban ? `${data.iban.slice(0, 4)}...${data.iban.slice(-4)}` : ""
        const shortenedBankAndBic = data.bank ? `${data.bank.slice(0, 20)}... ${data.bic}` : ""
        const editUrl = URL_REGISTER_SEPA_EDIT_ID.replace(":id", data.id).replace(":bookerId", bookerId)

        return (
            <div className={css.sepaData}>
                <div>
                    <div>{`${data.sepaAccountholder} ${shortenedIban}`}</div>
                    <div>{shortenedBankAndBic}</div>
                </div>
                <Link
                    to={{
                        pathname: editUrl,
                        state: { fromUrl: { pathname: location.pathname }, bookerId },
                    }}
                >
                    <Button icon={<EditOutlined />} type='link' />
                </Link>
            </div>
        )
    }

    return (
        <div>
            <Card
                // style={{ margin: margin.s }}
                actions={[
                    <Link
                        to={{
                            pathname: fromUrl?.pathname ?? URL_ACCOUNT,
                        }}
                        key='edit'
                    >
                        <Button type='text' danger key='cancel' style={{ fontWeight: 600 }}>
                            Abrechen
                        </Button>
                    </Link>,
                ]}
            >
                {sepaPaymentMethods?.map((pm) => (
                    <div key={pm.id}>
                        <SepaDataRender data={pm} />
                    </div>
                ))}
            </Card>
        </div>
    )
}

export default memo(SepaDataEdit)
